/**
 * 型定義ファイル
 * C#のモデルとJavaScript/TypeScript間でやり取りするデータ構造を定義
 */

/**
 * テキストの行揃え（複数行テキスト内での各行の揃え）
 */
export enum TextAlignment {
    Left = 0,
    Center = 1,
    Right = 2
}

/**
 * 水平方向の配置
 */
export enum HorizontalAlignment {
    Left = 0,
    Center = 1,
    Right = 2
}

/**
 * 垂直方向の配置
 */
export enum VerticalAlignment {
    Top = 0,
    Center = 1,
    Bottom = 2
}

/**
 * レーザー彫刻の設定
 */
export interface EngraveSettings {
    /** 彫刻する文字列 */
    text: string;
    /** 使用するフォント名 */
    fontFamily: string;
    /** フォントサイズ（ポイント） */
    fontSize: number;
    /** テキストの行揃え（複数行テキスト内での各行の揃え） */
    alignment: TextAlignment;
    /** 太字にするかどうか */
    isBold: boolean;
    /** 斜体にするかどうか */
    isItalic: boolean;
    /** 縦書きにするかどうか（false: 横書き, true: 縦書き） */
    isVertical: boolean;
    /** 水平方向の配置 */
    horizontalAlignment: HorizontalAlignment;
    /** 垂直方向の配置 */
    verticalAlignment: VerticalAlignment;
    /** 画像モードかどうか（true: 画像モード, false: テキストモード） */
    isImageMode?: boolean;
    /** 画像データ（base64エンコード済み） */
    imageData?: string;
    /** 画像ファイル名 */
    imageFileName?: string;
    /** 画像バリデーション警告（LocalStorage復元時に使用） */
    imageValidationWarnings?: string[];
}

/**
 * テキスト位置情報
 */
export interface TextPosition {
    /** X座標（Canvas座標系、px） */
    x: number;
    /** Y座標（Canvas座標系、px） */
    y: number;
    /** テキスト内容 */
    text: string;
}

/**
 * テキストレイアウト情報
 */
export interface TextLayout {
    /** テキストを行に分割したもの */
    lines: string[];
    /** 各行の位置情報 */
    positions: TextPosition[];
    /** フォントファミリー */
    fontFamily: string;
    /** フォントサイズ（ポイント） */
    fontSize: number;
    /** 行揃え（"left", "center", "right"） */
    alignment: string;
    /** 太字フラグ */
    isBold: boolean;
    /** 斜体フラグ */
    isItalic: boolean;
    /** 縦書きフラグ */
    isVertical: boolean;
}

/**
 * ストラップ寸法情報
 */
export interface StrapDimensions {
    /** 実寸の幅（mm） */
    widthMm: number;
    /** 実寸の高さ（mm） */
    heightMm: number;
    /** Canvas上での幅（px） */
    canvasWidth: number;
    /** Canvas上での高さ（px） */
    canvasHeight: number;
    /** 彫刻可能領域の幅（mm） */
    engravableWidthMm: number;
    /** 彫刻可能領域の高さ（mm） */
    engravableHeightMm: number;
    /** 彫刻可能領域の幅（px） */
    engravableWidthPx: number;
    /** 彫刻可能領域の高さ（px） */
    engravableHeightPx: number;
}

/**
 * バリデーション結果
 */
export interface ValidationResult {
    /** バリデーションが成功したかどうか */
    isValid: boolean;
    /** エラーメッセージのリスト */
    errors: string[];
    /** 警告メッセージのリスト */
    warnings: string[];
}

/**
 * テキストブロックの寸法
 */
export interface TextBlockDimensions {
    /** 幅（mm） */
    widthMm: number;
    /** 高さ（mm） */
    heightMm: number;
}

/**
 * 拡大時のズーム倍率（この値を変更すると全体の拡大倍率が変わります）
 */
export const ZOOM_SCALE_VALUE = 4 as const;

/**
 * ズームレベル（倍率）
 */
export type ZoomLevel = 1 | typeof ZOOM_SCALE_VALUE;

/**
 * ストラップ寸法のデフォルト値
 * 原寸大表示: 1mm = 96/25.4px ≈ 3.78px (CSS標準)
 */
export const StrapDimensionsDefaults = {
    /**
     * 前面（彫刻面）のデフォルト寸法
     * 3Dプリント製バターストラップ: 縦61.2mm × 横24.6mm × 厚さ17mm
     * 彫刻可能領域: 幅19.5mm × 高さ40.4mm
     */
    FrontFace: {
        widthMm: 24.6,
        heightMm: 61.2,
        canvasWidth: Math.round(24.6 * 96 / 25.4),  // 93px
        canvasHeight: Math.round(61.2 * 96 / 25.4),  // 231px
        engravableWidthMm: 19.5,
        engravableHeightMm: 40.4,
        engravableWidthPx: Math.round(19.5 * 96 / 25.4),  // 74px
        engravableHeightPx: Math.round(40.4 * 96 / 25.4)  // 153px
    } as StrapDimensions
};

/**
 * ズームレベルに応じてStrapDimensionsをスケーリング
 * @param strap 元のStrapDimensions
 * @param zoomLevel ズームレベル（1 or ZOOM_SCALE_VALUE）
 * @returns スケーリングされたStrapDimensions
 */
export function getScaledStrapDimensions(strap: StrapDimensions, zoomLevel: ZoomLevel): StrapDimensions {
    return {
        // 実寸の値は変更しない
        widthMm: strap.widthMm,
        heightMm: strap.heightMm,
        engravableWidthMm: strap.engravableWidthMm,
        engravableHeightMm: strap.engravableHeightMm,
        // Canvas上のピクセル値をズーム倍率でスケーリング
        canvasWidth: Math.round(strap.canvasWidth * zoomLevel),
        canvasHeight: Math.round(strap.canvasHeight * zoomLevel),
        engravableWidthPx: Math.round(strap.engravableWidthPx * zoomLevel),
        engravableHeightPx: Math.round(strap.engravableHeightPx * zoomLevel)
    };
}
